import { Box, Stack, Typography } from "@mui/material";
import { teal } from "@mui/material/colors";
import { SxProps, Theme } from "@mui/material/styles";
import * as React from "react";
import { Container, mergeSx, parallaxSx, WpImage } from "./Section";

export type HeroProps = {
  title: React.ReactNode;
  /** 标题上方的小标题（可含小图标） */
  eyebrow?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** 按钮等操作区 */
  actions?: React.ReactNode;
  /** 右侧配图（md 以上并排，手机端在文字下方） */
  image?: WpImage;
  /** 自定义右侧内容（如多张叠放的图片），优先于 image */
  media?: React.ReactNode;
  /** 白色文字；有背景图时默认 true，无背景图但用 sx 设了深色/渐变背景时传 true */
  dark?: boolean;
  /** 背景图；设置后文字为白色并加深色遮罩 */
  bgImage?: string;
  /** 背景图遮罩，默认 rgba(0,0,0,0.55) */
  overlay?: string;
  /** 最小高度，内容垂直居中；有背景图时默认 { xs: 480, md: 560 }（与原页面头图一致） */
  minHeight?: number | string | Partial<Record<"xs" | "sm" | "md" | "lg" | "xl", number | string>>;
  /** 背景图视差（桌面端背景固定不动） */
  parallax?: boolean;
  /** 无背景图时的背景色，默认与首页头图一致的 teal[100] */
  bgcolor?: string;
  /** 标题语义标签，默认 h1（每页一个 h1） */
  titleComponent?: React.ElementType;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
};

/**
 * 页面头图区：标题 + 副标题 + 操作按钮，可带背景图或右侧配图
 */
export default function Hero(props: HeroProps): React.ReactElement {
  const {
    title,
    eyebrow,
    subtitle,
    actions,
    image,
    media,
    dark: darkProp,
    bgImage,
    overlay = "rgba(0,0,0,0.55)",
    minHeight,
    parallax,
    bgcolor = teal[100],
    titleComponent = "h1",
    sx,
    children,
  } = props;
  const hasBgImage = !!bgImage;
  const dark = darkProp ?? hasBgImage;
  const centered = !image && !media;

  return (
    <Box
      sx={mergeSx(
        {
          position: "relative",
          display: "flex",
          alignItems: "center",
          minHeight: minHeight ?? (hasBgImage ? { xs: 480, md: 560 } : undefined),
          bgcolor: hasBgImage ? "grey.800" : bgcolor,
          color: dark ? "common.white" : "text.primary",
          py: { xs: 8, md: 14 },
        },
        hasBgImage && {
          backgroundImage: `url("${bgImage}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        },
        hasBgImage && parallax && parallaxSx,
        sx
      )}
    >
      {hasBgImage && (
        <Box sx={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0, bgcolor: overlay }} />
      )}
      <Container
        size="lg"
        sx={{
          position: "relative",
          width: "100%",
          display: "flex",
          alignItems: "center",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 4, md: 6 },
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: { xs: "100%", md: 0 },
            maxWidth: centered ? 860 : undefined,
            mx: "auto",
            textAlign: centered ? "center" : { xs: "center", md: "left" },
          }}
        >
          {eyebrow && (
            <Typography
              variant="subtitle1"
              component="div"
              sx={{ mb: 1.5, fontWeight: 500, color: dark ? "rgba(255,255,255,0.85)" : "text.secondary" }}
            >
              {eyebrow}
            </Typography>
          )}
          <Typography
            variant="h2"
            component={titleComponent}
            color="inherit"
            sx={{ typography: { xs: "h3", md: "h2" }, lineHeight: { xs: 1.2, md: 1.2 } }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body1"
              component="p"
              sx={{
                mt: 2,
                fontSize: { xs: "1rem", md: "1.125rem" },
                color: dark ? "rgba(255,255,255,0.85)" : "text.secondary",
              }}
            >
              {subtitle}
            </Typography>
          )}
          {actions && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 4, justifyContent: centered ? "center" : { xs: "center", md: "flex-start" } }}
            >
              {actions}
            </Stack>
          )}
          {children}
        </Box>
        {media ? (
          <Box sx={{ flex: 1, width: { xs: "100%", md: 0 }, textAlign: "center" }}>{media}</Box>
        ) : image && (
          <Box sx={{ flex: 1, width: { xs: "100%", md: 0 }, textAlign: "center" }}>
            <Box
              component="img"
              src={image.src}
              alt={image.alt || ""}
              width={image.width}
              height={image.height}
              sx={{ maxWidth: "100%", height: "auto", verticalAlign: "middle" }}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
}
